import React, { useState, useRef, useEffect } from "react";
import { BsX, BsRobot, BsSendFill } from "react-icons/bs";
import { assistantApi } from "../api/services";
import { FALLBACK_OPTIONS, STARTER_PROMPTS, WELCOME_TEXT } from "../constants/assistant";

const NAVY = "#071A33";
const ORANGE = "#FFB347";

let uid = 0;
const nextId = () => `m${Date.now()}_${uid++}`;

export default function SGOfficeAssistant({ onNavigate }) {
  const [open, setOpen] = useState(false);
  const [teaser, setTeaser] = useState(false);
  const [typing, setTyping] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [messages, setMessages] = useState([
    { id: nextId(), sender: "bot", text: WELCOME_TEXT, options: STARTER_PROMPTS },
  ]);
  const messagesRef = useRef(messages);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, typing, open]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    if (open) return undefined;
    const t = setTimeout(() => setTeaser(true), 5000);
    return () => clearTimeout(t);
  }, [open]);

  const pushUser = (text) => {
    setMessages((prev) => [...prev, { id: nextId(), sender: "user", text }]);
  };

  const goNavigate = (path) => {
    setOpen(false);
    if (onNavigate) onNavigate(path);
    else window.location.href = path;
  };

  const callAssistant = async (userMessage) => {
    setTyping(true);
    try {
      const history = messagesRef.current
        .slice(-10)
        .map((m) => ({
          role: m.sender === "user" ? "user" : "assistant",
          content: m.text,
        }))
        .filter((m) => m.content?.trim());

      const res = await assistantApi.chat({
        message: userMessage,
        history,
      });
      const data = res?.data?.data;
      setMessages((prev) => [
        ...prev,
        {
          id: nextId(),
          sender: "bot",
          text:
            data?.reply ||
            "I can help with products, quotes, order tracking, and delivery. What do you need?",
          options: data?.options?.length ? data.options : FALLBACK_OPTIONS,
          productMatches: data?.productMatches || [],
          categoryMatches: data?.categoryMatches || [],
        },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          id: nextId(),
          sender: "bot",
          text:
            "I couldn’t fetch my AI response right now, but I can still help you quickly with the options below.",
          options: FALLBACK_OPTIONS,
          productMatches: [],
          categoryMatches: [],
        },
      ]);
    } finally {
      setTyping(false);
    }
  };

  const handleOption = (opt) => {
    if (opt.type === "link") {
      pushUser(opt.label);
      setTimeout(() => window.open(opt.url, "_blank", "noopener,noreferrer"), 200);
      return;
    }
    if (opt.type === "route") {
      pushUser(opt.label);
      goNavigate(opt.path);
      return;
    }
    if (opt.type === "prompt") {
      pushUser(opt.label);
      callAssistant(opt.label);
      return;
    }
  };

  const handleSend = () => {
    const value = inputValue.trim();
    if (!value) return;
    pushUser(value);
    setInputValue("");
    callAssistant(value);
  };

  const lastBotIndex = [...messages].reverse().findIndex((m) => m.sender === "bot");
  const lastBotMessageId =
    lastBotIndex === -1 ? null : messages[messages.length - 1 - lastBotIndex].id;

  return (
    <>
      <style>{`
        @keyframes sgoa-fade-in-up {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes sgoa-window-in {
          from { opacity: 0; transform: translateY(24px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes sgoa-pulse-ring {
          0% { box-shadow: 0 0 0 0 rgba(255,179,71,0.55); }
          70% { box-shadow: 0 0 0 14px rgba(255,179,71,0); }
          100% { box-shadow: 0 0 0 0 rgba(255,179,71,0); }
        }
        @keyframes sgoa-dot-bounce {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.5; }
          30% { transform: translateY(-4px); opacity: 1; }
        }
        @keyframes sgoa-teaser-in {
          from { opacity: 0; transform: translateY(8px) scale(0.9); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .sgoa-msg { animation: sgoa-fade-in-up 0.28s ease-out; }
        .sgoa-window { animation: sgoa-window-in 0.28s cubic-bezier(0.16,1,0.3,1); }
        .sgoa-fab { animation: sgoa-pulse-ring 2.4s infinite; }
        .sgoa-dot { animation: sgoa-dot-bounce 1.2s infinite; }
        .sgoa-teaser { animation: sgoa-teaser-in 0.25s ease-out; }
        .sgoa-scroll::-webkit-scrollbar { width: 6px; }
        .sgoa-scroll::-webkit-scrollbar-thumb { background: rgba(7,26,51,0.18); border-radius: 999px; }
        .sgoa-chip {
          transition: transform 0.15s ease, background 0.15s ease, border-color 0.15s ease;
        }
        .sgoa-chip:hover { transform: translateY(-1px); border-color: ${ORANGE}; background: #FFF6E9; }
        .sgoa-chip:active { transform: translateY(0) scale(0.98); }
        @media (max-width: 480px) {
          .sgoa-window-mobile {
            bottom: 16px !important;
            left: 16px !important;
            right: 16px !important;
            width: auto !important;
            max-width: none !important;
            height: min(560px, calc(100vh - 32px)) !important;
          }
          .sgoa-teaser-mobile {
            left: 16px !important;
            bottom: 88px !important;
            max-width: calc(100vw - 100px) !important;
          }
          .sgoa-fab-mobile {
            left: 16px !important;
            bottom: 16px !important;
          }
        }
      `}</style>

      {!open && teaser && (
        <div
          className="sgoa-teaser sgoa-teaser-mobile"
          style={{ position: "fixed", bottom: 100, left: 24, zIndex: 9998, maxWidth: 250 }}
        >
          <div
            style={{
              background: "rgba(255,255,255,0.92)",
              backdropFilter: "blur(14px)",
              border: "1px solid rgba(255,255,255,0.5)",
              borderRadius: 16,
              padding: "12px 14px",
              boxShadow: "0 12px 32px rgba(7,26,51,0.18)",
              fontSize: 13.5,
              color: NAVY,
              cursor: "pointer",
              position: "relative",
              lineHeight: 1.45,
            }}
            onClick={() => {
              setOpen(true);
              setTeaser(false);
            }}
          >
            <button
              type="button"
              aria-label="Dismiss"
              onClick={(e) => {
                e.stopPropagation();
                setTeaser(false);
              }}
              style={{
                position: "absolute",
                top: -6,
                right: -6,
                width: 20,
                height: 20,
                borderRadius: "50%",
                background: NAVY,
                color: "#fff",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <BsX size={13} />
            </button>
            Need products, a bulk quote, or order tracking? I can help — try asking!
          </div>
        </div>
      )}

      {!open && (
        <button
          type="button"
          aria-label="Open SG Office Assistant"
          onClick={() => {
            setOpen(true);
            setTeaser(false);
          }}
          className="sgoa-fab sgoa-fab-mobile"
          style={{
            position: "fixed",
            bottom: 24,
            left: 24,
            zIndex: 9999,
            width: 62,
            height: 62,
            borderRadius: "50%",
            border: "none",
            cursor: "pointer",
            background: `linear-gradient(145deg, ${NAVY}, #0d2c52)`,
            boxShadow: "0 10px 30px rgba(7,26,51,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <BsRobot color={ORANGE} size={28} />
        </button>
      )}

      {open && (
        <div
          className="sgoa-window sgoa-window-mobile"
          style={{
            position: "fixed",
            bottom: 24,
            left: 24,
            zIndex: 9999,
            width: 380,
            maxWidth: "calc(100vw - 32px)",
            height: 600,
            maxHeight: "calc(100vh - 48px)",
            display: "flex",
            flexDirection: "column",
            borderRadius: 24,
            overflow: "hidden",
            background: "rgba(255,255,255,0.96)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(255,255,255,0.6)",
            boxShadow: "0 24px 64px rgba(7,26,51,0.28)",
          }}
        >
          <div
            style={{
              background: `linear-gradient(135deg, ${NAVY}, #10315c)`,
              padding: "18px 18px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexShrink: 0,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: "50%",
                  background: `linear-gradient(145deg, ${ORANGE}, #ffcd7a)`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <BsRobot size={20} color={NAVY} />
              </div>
              <div>
                <div style={{ color: "#fff", fontWeight: 600, fontSize: 15, lineHeight: 1.2 }}>
                  SG Office Assistant
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3 }}>
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: "#3CD873",
                      boxShadow: "0 0 0 2px rgba(60,216,115,0.25)",
                      display: "inline-block",
                    }}
                  />
                  <span style={{ color: "rgba(255,255,255,0.75)", fontSize: 12 }}>
                    UAE office & school supplies
                  </span>
                </div>
              </div>
            </div>
            <button
              type="button"
              aria-label="Close chat"
              onClick={() => setOpen(false)}
              style={{
                background: "rgba(255,255,255,0.12)",
                border: "none",
                borderRadius: "50%",
                width: 30,
                height: 30,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <BsX size={18} color="#fff" />
            </button>
          </div>

          <div
            ref={scrollRef}
            className="sgoa-scroll"
            style={{
              flex: 1,
              overflowY: "auto",
              padding: "18px 16px",
              display: "flex",
              flexDirection: "column",
              gap: 10,
              background:
                "radial-gradient(circle at top right, rgba(255,179,71,0.08), transparent 55%), #fff",
            }}
          >
            {messages.map((m) => (
              <div key={m.id} className="sgoa-msg">
                <div
                  style={{
                    display: "flex",
                    justifyContent: m.sender === "user" ? "flex-end" : "flex-start",
                  }}
                >
                  <div
                    style={{
                      maxWidth: "88%",
                      padding: "10px 14px",
                      borderRadius:
                        m.sender === "user" ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                      fontSize: 13.5,
                      lineHeight: 1.5,
                      whiteSpace: "pre-line",
                      background:
                        m.sender === "user"
                          ? `linear-gradient(135deg, ${NAVY}, #123a6b)`
                          : "rgba(7,26,51,0.05)",
                      color: m.sender === "user" ? "#fff" : NAVY,
                      border: m.sender === "user" ? "none" : "1px solid rgba(7,26,51,0.06)",
                    }}
                  >
                    {m.text}
                  </div>
                </div>

                {m.sender === "bot" && m.options && m.id === lastBotMessageId && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 8,
                      marginTop: 10,
                      paddingLeft: 2,
                    }}
                  >
                    {m.options.map((opt) => (
                      <button
                        type="button"
                        key={opt.label}
                        className="sgoa-chip"
                        onClick={() => handleOption(opt)}
                        style={{
                          border: "1px solid rgba(7,26,51,0.15)",
                          background: "#fff",
                          color: NAVY,
                          fontSize: 12.5,
                          fontWeight: 500,
                          padding: "8px 13px",
                          borderRadius: 999,
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        {opt.icon && <opt.icon size={14} />}
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}

                {m.sender === "bot" && m.categoryMatches?.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                      marginTop: 10,
                      paddingLeft: 2,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: "#667085",
                      }}
                    >
                      Did you mean:
                    </span>
                    {m.categoryMatches.map((item) => (
                      <button
                        key={`${m.id}_cat_${item.slug}`}
                        type="button"
                        className="sgoa-chip"
                        onClick={() => {
                          pushUser(`Browse ${item.name}`);
                          goNavigate(`/products?category=${encodeURIComponent(item.slug)}`);
                        }}
                        style={{
                          border: "1px solid rgba(255,179,71,0.45)",
                          background: "#fff8ec",
                          color: NAVY,
                          fontSize: 12.5,
                          fontWeight: 600,
                          padding: "9px 12px",
                          borderRadius: 12,
                          cursor: "pointer",
                          textAlign: "left",
                        }}
                      >
                        {item.name}
                      </button>
                    ))}
                  </div>
                )}

                {m.sender === "bot" && m.productMatches?.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                      marginTop: 10,
                      paddingLeft: 2,
                    }}
                  >
                    {m.productMatches.map((item) => (
                      <button
                        key={`${m.id}_${item.slug}`}
                        type="button"
                        className="sgoa-chip"
                        onClick={() => {
                          pushUser(`Open ${item.name}`);
                          goNavigate(`/product/${item.slug}`);
                        }}
                        style={{
                          border: "1px solid rgba(7,26,51,0.12)",
                          background: "#fff",
                          color: NAVY,
                          fontSize: 12.5,
                          fontWeight: 600,
                          padding: "9px 12px",
                          borderRadius: 12,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 10,
                          textAlign: "left",
                        }}
                      >
                        <span style={{ flex: 1, minWidth: 0 }}>{item.name}</span>
                        <strong style={{ whiteSpace: "nowrap" }}>
                          AED {Number(item.price || 0).toFixed(2)}
                        </strong>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {typing && (
              <div style={{ display: "flex", justifyContent: "flex-start" }}>
                <div
                  style={{
                    background: "rgba(7,26,51,0.05)",
                    border: "1px solid rgba(7,26,51,0.06)",
                    borderRadius: "16px 16px 16px 4px",
                    padding: "12px 16px",
                    display: "flex",
                    gap: 4,
                  }}
                >
                  {[0, 1, 2].map((i) => (
                    <span
                      key={i}
                      className="sgoa-dot"
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        background: NAVY,
                        animationDelay: `${i * 0.15}s`,
                        display: "inline-block",
                      }}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div
            style={{
              flexShrink: 0,
              padding: 12,
              borderTop: "1px solid rgba(7,26,51,0.08)",
              display: "flex",
              gap: 8,
              background: "rgba(255,255,255,0.85)",
            }}
          >
            <input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSend()}
              placeholder='Try "track order" or "bulk quote"...'
              style={{
                flex: 1,
                border: "1px solid rgba(7,26,51,0.15)",
                borderRadius: 999,
                padding: "10px 16px",
                fontSize: 13.5,
                outline: "none",
                background: "#fff",
                color: NAVY,
              }}
            />
            <button
              type="button"
              aria-label="Send message"
              onClick={handleSend}
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                border: "none",
                background: ORANGE,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              <BsSendFill size={14} color={NAVY} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
